import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useTanstackQuestionnairesTypeFetchFn } from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { ApiError } from '@/components/Functional/lib/request'
import { DialogQuestionnaire } from '@/components/Layout/questionnaire/DialogQuestionnaire'
import type { QuestionnaireAnswerFormData } from '@/schemas/forms/questionnaire'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  opened: boolean
  onAnswered: () => void
  onDismissed: () => void
}

/**
 * 特典獲得フロー用アンケートコンポーネント。
 * 未回答の場合にアンケートダイアログを表示し、回答後に onAnswered を呼ぶ。
 * アンケートなし or 回答済みの場合は直ちに onAnswered を呼ぶ。
 */
const RewardsQuestionnaire = ({ opened, onAnswered, onDismissed }: Props) => {
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { t } = useTranslation()

  const resolveErrorMessage = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        return t('register_questionnaire.submit_error_conflict', 'このアンケートはすでに回答済みです。')
      }
      if (err.status === 400 || err.status === 422) {
        const serverMessage = (err.body as { error?: { message?: string } } | undefined)?.error?.message
        if (serverMessage) return serverMessage
        return t(
          'register_questionnaire.submit_error_validation',
          '入力内容にエラーがあります。内容を確認してください。',
        )
      }
    }
    return t('register_questionnaire.submit_error', '送信に失敗しました。もう一度お試しください。')
  }

  const questionnairesTypeQuery = useTanstackQuestionnairesTypeFetchFn('reward')
  const {
    data: questionnaireData,
    isLoading,
    isError,
  } = useQuery({
    ...questionnairesTypeQuery,
    enabled: opened,
    // loadData でプリフェッチ済みのキャッシュを再利用するため、
    // 取得から60秒以内は再フェッチしない（staleTime: 0 のデフォルトを上書き）。
    // 回答後は invalidateQueries(['questionnaires', 'type']) で無効化されるため鮮度は保たれる。
    staleTime: 60 * 1000,
    // 404（アンケートなし）はリトライせず即座に次フローへ移行する。
    // 他のエラーも1回のみリトライして過剰なリクエストを防ぐ。
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false
      return failureCount < 1
    },
  })

  const api = useApi()

  const questionnaire = questionnaireData?.questionnaire ?? null

  // データ取得完了後、回答済み or アンケートなし or エラー（404含む）ならそのまま特典取得フローへ
  useEffect(() => {
    if (!opened || isLoading || submitted) return
    if (isError || questionnaire === null || questionnaire.answered) {
      onAnswered()
    }
  }, [opened, isLoading, isError, questionnaire, submitted, onAnswered])

  const isDialogOpen =
    opened && !isLoading && !isError && questionnaire !== null && !questionnaire.answered && !submitted

  const handleSubmit = async (formData: QuestionnaireAnswerFormData): Promise<boolean> => {
    const questionnaireId = questionnaire?.id
    if (!questionnaireId) {
      setSubmitted(true)
      onAnswered()
      return true
    }
    const answers = Object.entries(formData).map(([itemId, value]) => ({
      itemId: Number.parseInt(itemId, 10),
      answer: Array.isArray(value) ? value : value ? [value] : [],
    }))
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await api.auth.postQuestionnaireAnswer(questionnaireId, {
        questionnaireId,
        answers,
      })
      setSubmitted(true)
      onAnswered()
      return true
    } catch (err) {
      setSubmitError(resolveErrorMessage(err))
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!opened || isLoading) return null

  return (
    <DialogQuestionnaire
      questionnaire={questionnaire}
      isOpen={isDialogOpen}
      isSubmitting={isSubmitting}
      submitError={submitError}
      onClose={onDismissed}
      onSubmit={handleSubmit}
    />
  )
}

export default RewardsQuestionnaire
